import Navigo from "navigo";
import Lockr from "lockr";
import qs from "qs";
import moment from "moment";
import { Api } from "./services/Api";
import Toast from "./helpers/toaster";
import { Intent } from "@blueprintjs/core";
import { toJS } from "mobx";
import { searchFromQuery } from "./helpers/searchQuery";

//let root = null;
let root = window.location.origin;
let useHash = false;
let hash = "#";

const startRouter = (stores) => {
  let router = new Navigo(root, useHash, hash);

  // What the users list last loaded (section + filters), see /dashboard/users/:section.
  let lastUsersLoadKey = null;

  // The users map was removed; its old links open the users list instead.
  const openAllUsers = () => {
    stores.viewStore.userSearch = { ...stores.viewStore.userSearch, section: "all" };
    stores.viewStore.users();
  };

  // Defaults for each list's search, captured before any route runs so a URL
  // that sets only some filters keeps the rest (see searchFromQuery).
  const searchDefaults = {};
  [
    "bookingSearch",
    "clientSearch",
    "clientShowSearch",
    "equipmentSearch",
    "equipmentShowSearch",
    "formShowSearch",
    "frequencySearch",
    "geolocationSearch",
    "invoiceSearch",
    "jobSearch",
    "materialLineItemSearch",
    "pricingParameterSearch",
    "projectSearch",
    "projectShowSearch",
    "purchaseOrderItemSearch",
    "questionGroupSearch",
    "questionSearch",
    "quoteSearch",
    "serviceAgreementSearch",
    "serviceTypeExtraSearch",
    "serviceTypeSearch",
    "supplierSearch",
    "timesheetSearch",
    "userSearch",
    "userShowSearch",
    "workReportSearch",
  ].forEach((key) => {
    searchDefaults[key] = toJS(stores.viewStore[key]);
  });
  const applySearchQuery = (key, query) => {
    stores.viewStore[key] = searchFromQuery(searchDefaults[key], query);
  };
  let processLoggedInBehaviour = {
    before: (done, params) => {
      const currentUser = Lockr.get("currentUser");
      if (currentUser) {
        if (currentUser.attributes?.role === "technician") {
          stores.viewStore.technicianQuotes();
          router.navigate(
            `technician/quotes?${qs.stringify(stores.viewStore.quoteSearch, {
              encode: false,
            })}`
          );
        } else {
          stores.viewStore.dashboard();
          router.navigate("/");
        }
        Toast.show({
          message: "You are already logged in!",
          intent: Intent.DANGER,
        });
        // done();
      } else {
        done();
      }
    },
  };

  let checkAndProcessTechnicianBehaviour = {
    before: (done, params) => {
      const currentUser = Lockr.get("currentUser");
      if (currentUser?.attributes?.role === "technician") {
        stores.viewStore.technicianQuotes();
        router.navigate(
          `technician/quotes?${qs.stringify(stores.viewStore.quoteSearch, {
            encode: false,
          })}`
        );
        // done();
      } else {
        done();
      }
    },
  };

  router.hooks({
    before: (done, params) => {
      stores.viewStore.routingParams = params;
      if (!stores.viewStore.currentUser) {
        stores.viewStore.setCurrentUser();
      }
      done();
    },
  });

  router
    .on(
      "/",
      () => {
        //
      },
      {
        before: (done, params) => {
          const currentUser = Lockr.get("currentUser");
          if (currentUser) {
            if (currentUser.attributes?.role === "technician") {
              stores.viewStore.technicianQuotes();
            } else {
              stores.viewStore.dashboard();
            }
            done();
          } else {
            router.navigate("/login");
          }
        },
      }
    )
    .on(
      "/login",
      () => {
        stores.viewStore.login();
      },
      processLoggedInBehaviour
    )
    .on("/quickbooks/auth", () => {
      stores.viewStore.quickbooksAuth();
    })
    .on("/invitation-accepted/download", () => {
      stores.viewStore.invitationAccepted();
    })
    .on(
      "/signup",
      () => {
        stores.viewStore.signup();
      },
      processLoggedInBehaviour
    )
    .on(
      "/signup/company",
      () => {
        stores.viewStore.signupCompany();
      },
      processLoggedInBehaviour
    )
    .on(
      "/signup/company/type",
      () => {
        stores.viewStore.signupCompanyType();
      },
      processLoggedInBehaviour
    )
    .on(
      "/signup/company/team",
      () => {
        stores.viewStore.signupCompanyTeamSize();
      },
      processLoggedInBehaviour
    )
    .on(
      "/signup/details",
      () => {
        stores.viewStore.signupDetails();
      },
      processLoggedInBehaviour
    )
    .on("/invite/users", () => {
      // Keep the signup flag when the URL catches up with inviteUsers().
      stores.viewStore.inviteUsers({ onboarding: stores.viewStore.inviteOnboarding });
    })
    .on("/logout", () => {
      stores.viewStore.performLogout();
    })
    .on("/logged-out", () => {
      stores.viewStore.performLogout();
    })
    .on("/forget-password", () => {
      stores.viewStore.forgetPassword();
    })
    .on(
      "/password-reset/:token",
      (params, query) => {
        stores.viewStore.resetPassword(params);
      },
      {
        before: (done, params) => {
          let promise = Api.post(`/password/verify_token`, {
            token: params.token,
          });

          promise
            .then((response) => {
              done();
            })
            .catch((error) => {
              router.navigate("/password/expired");
            });
        },
      }
    )
    .on("/password/expired", () => {
      stores.viewStore.invalidResetPasswordToken();
    })
    .on(
      "/dashboard",
      (params, query) => {
        stores.viewStore.dashboardSearch = qs.parse(query);
        // stores.dashboard.loadUsers({ section: 'all' });
        // stores.dashboard.loadJobs();
        stores.viewStore.setCurrentUser();
        stores.viewStore.dashboard();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/map",
      (params, query) => {
        const search = qs.parse(query);
        stores.viewStore.dashboardSearch = {
          ...search,
          date: search.date || moment().unix().toString(),
        };
        stores.viewStore.setCurrentUser();
        stores.viewStore.dashboardMap();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/assignments",
      (params, query) => {
        // The sidebar links here without a query; keep the filters in use.
        if (query) applySearchQuery("jobSearch", query);
        stores.viewStore.assignments();
        stores.jobStore.loadAssignments(params, query);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/jobs",
      (params, query) => {
        // The sidebar links here without a query; keep the filters in use.
        if (query) applySearchQuery("jobSearch", query);
        stores.viewStore.jobs();
        stores.jobStore.loadJobs(params, query);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/jobs/new",
      (params, query) => {
        stores.viewStore.AddNewJob();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/jobs/:uuid",
      (params, query) => {
        stores.viewStore.ShowJob(params, query);
        stores.jobStore.loadJob(params, query);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/jobs/:uuid/:section",
      (params, query) => {
        stores.viewStore.ShowJob(params, query);
        stores.jobStore.loadJob(params, query);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/dashboard/users/map-show", openAllUsers, checkAndProcessTechnicianBehaviour)
    .on("/dashboard/users/map", openAllUsers, checkAndProcessTechnicianBehaviour)
    .on(
      "/dashboard/users/vacations",
      (params, query) => {
        stores.viewStore.usersVacations(params);
        stores.userStore.loadUsersVacations();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/users/profile",
      (params, query) => {
        stores.viewStore.userProfile(params);
        stores.userStore.loadUser(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/dashboard/users/change-password", (params, query) => {
      let currentUser = Lockr.get("currentUser");
      if (params === "") {
        stores.viewStore.changePassword(params);
        stores.userStore.loadUser(params);
      } else {
        stores.viewStore.changePassword(params);
        stores.userStore.loadUser({ uuid: currentUser.id });
      }
    })
    .on(
      "/dashboard/users/:section",
      (params, query) => {
        // Filters come from the query (see viewStore.users). The old
        // /users/admin and /users/technician links preselect that user type.
        const role = ["admin", "technician"].includes(params.section) ? params.section : null;
        const section = params.section === "invitations" ? "invitations" : "all";
        const search = { ...searchFromQuery(searchDefaults.userSearch, query), section };
        search.term = search.term === undefined || search.term === null ? "" : String(search.term);
        if (role) search.where = { ...search.where, role: [role] };

        // Typing only changes `term`, which filters the loaded list in the
        // browser, so don't hit the API again for it.
        const loadKey = JSON.stringify({ section, where: search.where });
        const sameList =
          stores.viewStore.currentView === "Pages.Users.Index" && loadKey === lastUsersLoadKey;
        lastUsersLoadKey = loadKey;

        stores.viewStore.userSearch = search;
        stores.viewStore.users();
        if (!sameList) stores.dashboard.loadUsers({ section, where: search.where });
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/users/:uuid/show",
      (params, query) => {
        applySearchQuery("userShowSearch", query);
        stores.viewStore.userShow(params);
        stores.userStore.loadUser(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/users/invitation/:token",
      (params, query) => {
        stores.viewStore.userInvitation(params);
        stores.userStore.loadUserInvitation(params);
      },
      processLoggedInBehaviour
    )
    .on(
      "/dashboard/projects",
      (params, query) => {
        applySearchQuery("projectSearch", query);
        stores.viewStore.projects();
        stores.projectStore.loadProjects();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/projects/:uuid/show",
      (params, query) => {
        applySearchQuery("projectShowSearch", query);
        stores.viewStore.projectShow(params);
        stores.projectStore.loadProject(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/work-reports",
      (params, query) => {
        applySearchQuery("workReportSearch", query);
        stores.viewStore.workReports();
        stores.workReportStore.loadWorkReports();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/work-reports/:uuid/show",
      (params, query) => {
        stores.viewStore.ShowWorkOrder(params);
        stores.workReportStore.loadWorkReport(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/purchase-orders/:uuid/show-purchase-order",
      (params, query) => {
        stores.viewStore.ShowPurchaseOrder(params);
        stores.workReportStore.loadPurchaseOrder(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/quotes",
      (params, query) => {
        applySearchQuery("quoteSearch", query);
        stores.viewStore.quotes();
        stores.quoteStore.loadQuotes();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/quotes/:uuid/show",
      (params, query) => {
        stores.viewStore.ShowQuote(params);
        stores.quoteStore.loadQuote(params);
        stores.taxStore.loadTaxes();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/invoices",
      (params, query) => {
        applySearchQuery("invoiceSearch", query);
        stores.viewStore.invoices();
        stores.invoiceStore.loadInvoices();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/invoices/:uuid/show",
      (params, query) => {
        stores.viewStore.ShowInvoice(params);
        stores.invoiceStore.getInvoice(params);
        stores.taxStore.loadTaxes();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/clients",
      (params, query) => {
        // Restore the search from the URL (e.g. coming Back from a record).
        applySearchQuery("clientSearch", query);
        stores.viewStore.clients();
        stores.clientStore.loadClients();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/suppliers",
      (params, query) => {
        // Restore the search from the URL (e.g. coming Back from a record).
        applySearchQuery("supplierSearch", query);
        stores.viewStore.suppliers();
        stores.supplierStore.loadSuppliers();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/suppliers/:uuid/show",
      (params, query) => {
        stores.viewStore.supplierShow(params);
        stores.supplierStore.loadSupplier(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/equipments",
      (params, query) => {
        // Restore the search from the URL (e.g. coming Back from a record).
        applySearchQuery("equipmentSearch", query);
        stores.viewStore.equipments();
        stores.equipmentStore.loadEquipments();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/equipments/:uuid/show",
      (params, query) => {
        applySearchQuery("equipmentShowSearch", query);
        stores.viewStore.equipmentShow(params);
        stores.equipmentStore.loadEquipment(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service-type",
      (params, query) => {
        stores.viewStore.serviceTypes();
        stores.serviceTypeStore.loadServiceTypes();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service-type/:uuid/show",
      (params, query) => {
        applySearchQuery("serviceTypeSearch", query);
        stores.viewStore.serviceTypeShow(params);
        stores.serviceTypeStore.loadServiceType(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service-type/add",
      (params, query) => {
        //applySearchQuery("serviceTypeSearch", query);
        stores.viewStore.serviceTypeShow(params);
        //stores.serviceTypeStore.loadServiceType(params);
      },
      checkAndProcessTechnicianBehaviour
    )

    .on(
      "/dashboard/pricing-parameter",
      (params, query) => {
        stores.viewStore.pricingParameters();
        stores.pricingParameterStore.loadPricingParameters();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/pricing-parameter/:uuid/show",
      (params, query) => {
        applySearchQuery("pricingParameterSearch", query);
        stores.viewStore.pricingParameterShow(params);
        // stores.pricingParameterStore.loadPricingParameter(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/pricing-parameter/add",
      (params, query) => {
        stores.viewStore.pricingParameterAdd(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question",
      (params, query) => {
        stores.viewStore.questions();
        stores.questionStore.loadQuestions();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question/:uuid/show",
      (params, query) => {
        applySearchQuery("questionSearch", query);
        stores.viewStore.questionShow(params);
        stores.questionStore.loadQuestion(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question/add",
      (params, query) => {
        stores.viewStore.questionAdd(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question-group",
      (params, query) => {
        stores.viewStore.questionGroups();
        stores.questionGroupStore.loadQuestionGroups();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question-group/:uuid/show",
      (params, query) => {
        applySearchQuery("questionGroupSearch", query);
        stores.viewStore.questionGroupShow(params);
        stores.questionGroupStore.loadQuestionGroup(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/question-group/add",
      (params, query) => {
        stores.viewStore.questionGroupAdd(params);
      },
      checkAndProcessTechnicianBehaviour
    )

    .on(
      "/dashboard/service-type-extra",
      (params, query) => {
        stores.viewStore.serviceTypeExtras();
        stores.serviceTypeExtraStore.loadServiceTypeExtras();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service-type-extra/:uuid/show",
      (params, query) => {
        applySearchQuery("serviceTypeExtraSearch", query);
        stores.viewStore.serviceTypeExtraShow(params);
        stores.serviceTypeExtraStore.loadServiceTypeExtra(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service-type-extra/add",
      (params, query) => {
        stores.viewStore.serviceTypeExtraAdd(params);
      },
      checkAndProcessTechnicianBehaviour
    )

    .on(
      "/dashboard/frequency",
      (params, query) => {
        stores.viewStore.frequencies();
        stores.frequencyStore.loadFrequencies();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/frequency/:uuid/show",
      (params, query) => {
        applySearchQuery("frequencySearch", query);
        stores.viewStore.frequencyShow(params);
        stores.frequencyStore.loadFrequency(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/frequency/add",
      (params, query) => {
        stores.viewStore.frequencyAdd(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/spots/add", (params, query) => {
      stores.viewStore.spotAdd(params);
    })

    .on(
      "/dashboard/forms",
      (params, query) => {
        stores.viewStore.forms();
        stores.formStore.loadForms();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/forms/:uuid",
      (params, query) => {
        applySearchQuery("formShowSearch", query);
        stores.viewStore.formShow(params);
        stores.formStore.loadForm(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/clients/:uuid",
      (params, query) => {
        applySearchQuery("clientShowSearch", query);
        // Switching tabs only changes ?resource: keep the loaded client (and
        // any unsaved edits) instead of fetching it again.
        const loaded = stores.clientStore.client.object;
        if (loaded && loaded.id === params.uuid) {
          stores.viewStore.clientShow(params, loaded.attributes && loaded.attributes.name);
          return;
        }
        stores.viewStore.clientShow(params);
        stores.clientStore.loadClient(params);
        stores.clientStore.loadAnalytics(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/timesheets",
      (params, query) => {
        applySearchQuery("timesheetSearch", query);
        stores.viewStore.timesheets();
        stores.timesheetStore.loadTimelogs();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/materialorders",
      (params, query) => {
        applySearchQuery("materialLineItemSearch", query);
        stores.viewStore.MaterialOrder();
        // stores.materialorderStore.loadMaterialOrder();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/purchase-orders",
      (params, query) => {
        applySearchQuery("purchaseOrderItemSearch", query);
        stores.viewStore.PurchaseOrder();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/billing", (params, query) => {
      stores.viewStore.billingInfo = qs.parse(query);
      stores.viewStore.loadBillingOverview();
    })
    .on("/taxes", (params, query) => {
      stores.viewStore.loadTaxes();
    })
    .on("/payment_methods", (params, query) => {
      stores.viewStore.loadPaymentMethods();
    })
    .on("/products_services", (params, query) => {
      stores.viewStore.loadProductsServices();
    })
    .on("/product_category", (params, query) => {
      stores.viewStore.loadProductCategory();
    })
    .on("/service_type_category", (params, query) => {
      stores.viewStore.loadServiceTypeCategory();
    })
    .on("/preferences", (params, query) => {
      stores.viewStore.loadPreferences();
    })
    .on("/preferences/default_line_items", (params, query) => {
      stores.viewStore.loadDefaultLineItems();
    })
    .on("/preferences/accounting_setup", (params, query) => {
      stores.viewStore.accountingSetup();
    })
    .on(
      "/dashboard/service_agreements",
      (params, query) => {
        stores.viewStore.serviceAgreements();
        stores.serviceAgreementStore.loadServiceAgreements();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/service_agreements/:uuid/show",
      (params, query) => {
        applySearchQuery("serviceAgreementSearch", query);
        stores.viewStore.serviceAgreementShow(params);
        stores.serviceAgreementStore.loadServiceAgreement(params);
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/preferences/invoice_settings", (params, query) => {
      stores.viewStore.invoiceSettings();
    })
    .on("/preferences/company_details", (params, query) => {
      stores.viewStore.companyDetails();
    })
    .on("/preferences/scope_of_work_templates", (params, query) => {
      stores.viewStore.scopeOfWorkTemplates();
    })
    .on("/preferences/scope_of_work_templates/:uuid/edit", (params, query) => {
      stores.viewStore.scopeOfWorkTemplatesEdit(params, query);
    })
    .on(
      "/dashboard/bookings/:uuid/show",
      (params, query) => {
        stores.viewStore.bookingShow(params, query);
        stores.bookingStore.loadBooking(params).then((response) => {
          stores.serviceTypeStore
            .loadServiceTypeDetail({
              uuid: response.data.data.attributes.service_type_id,
            })
            .then((response) => {
              stores.serviceTypeStore.loadPricingParameter();
            });
        // The page shows a not-found state from bookingStore.booking.isError.
        }).catch(() => {});
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/bookings",
      (params, query) => {
        applySearchQuery("bookingSearch", query);
        stores.viewStore.bookings();
        stores.bookingStore.loadBookings();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/bookings/share",
      () => {
        stores.viewStore.bookingShare();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/bookings/booking-configuration",
      (params, query) => {
        stores.viewStore.bookingConfigurations();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on(
      "/dashboard/bookings/new",
      (params, query) => {
        stores.viewStore.newBooking();
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/pay/:tenant_id/invoices/:invoice_id", (params, query) => {
      stores.viewStore.payInvoice(params);
    })
    .on(
      "/booking_public/:tenant_id/bookings/:booking_id/show",
      (params, query) => {
        stores.viewStore.showPublicBooking(params);
        stores.bookingStore.loadBookingForPublicLink(params);
      }
    )
    .on("/booking_public/:tenant_id/bookings/new", (params, query) => {
      stores.viewStore.newPublicBooking(params);
      stores.bookingStore.loadTenantForPublicLink(params);
    })
    .on(
      "/booking_public/:tenant_id/bookings/booking-modal",
      (params, query) => {
        stores.viewStore.newPublicModalBooking(params);
        stores.bookingStore.loadTenantForPublicModal(params);
      }
    )
    .on("/booking-confirmation", (params, query) => {
      stores.viewStore.bookingConfirmationMessage(params);
    })
    .on("/review/:tenant_id/quotes/:work_report_id", (params, query) => {
      stores.viewStore.reviewQuote(params);
    })
    .on(
      "/forms/:tenant_id/form_submission/:form_submission_id/:token",
      (params, query) => {
        stores.viewStore.formSubmission(params);
      }
    )
    .on(
      "/dashboard/notifications",
      (params, query) => {
        stores.viewStore.notifications();
        //stores.notificationStore.loadNotifications(params, query)
      },
      checkAndProcessTechnicianBehaviour
    )
    .on("/technician/quotes", (params, query) => {
      applySearchQuery("quoteSearch", query);
      stores.viewStore.technicianQuotes();
      stores.quoteStore.loadQuotes();
    })
    .on("/technician/quotes/:uuid/show", (params, query) => {
      stores.viewStore.showTechnicianQuote(params);
      stores.quoteStore.loadQuote(params);
      stores.taxStore.loadTaxes();
    })
    .resolve();

  return router;
};

export default startRouter;
